// PRD.284 — Format an encounter's vitals into a compact one-line summary for
// the printed prescription (e.g. "Blood Pressure 120/80 mmHg · Pulse 78 bpm ·
// Temp 37.2 °C"). Vitals are definition-driven (any set of vital types), so this
// stays generic: latest value per definition, ordered by the definition's
// sort_order.
//
// Self-contained (no PowerSync/query imports) so the pure logic is unit-testable.

/** Minimal structural shape of a vitals row (from qVitalsByEncounterId). */
export interface VitalSummaryEntry {
  vital_definition_id: string;
  code?: string | null;
  name?: string | null;
  unit?: string | null;
  value_number?: number | null;
  value_text?: string | null;
  value_boolean?: number | null;
  sort_order?: number | null;
  recorded_at?: string | null;
}

function formatValue(e: VitalSummaryEntry): string {
  if (e.value_number != null && Number.isFinite(e.value_number)) {
    // Trim trailing zeros: 37.20 → "37.2", 78 → "78".
    return String(Number(e.value_number));
  }
  if (typeof e.value_text === 'string' && e.value_text.trim()) return e.value_text.trim();
  if (e.value_boolean != null) return e.value_boolean === 1 ? 'Yes' : 'No';
  return '';
}

/**
 * Build a single-line vitals summary. Keeps only the most recently recorded
 * entry per vital definition, orders by `sort_order`, and joins non-empty
 * values with " · ". Returns '' when there are no usable vitals.
 */
export function formatVitalsSummary(rows: VitalSummaryEntry[]): string {
  // Latest entry per definition. Rows may arrive in any order, so compare
  // recorded_at rather than relying on the query's DESC ordering.
  const latest = new Map<string, VitalSummaryEntry>();
  for (const r of rows) {
    const key = r.vital_definition_id || r.code || '';
    if (!key) continue;
    const existing = latest.get(key);
    if (!existing || (r.recorded_at ?? '') > (existing.recorded_at ?? '')) {
      latest.set(key, r);
    }
  }

  return [...latest.values()]
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .map((e) => {
      const value = formatValue(e);
      if (!value) return '';
      const label = (e.name || e.code || '').trim();
      const unit = (e.unit ?? '').trim();
      return `${label ? `${label} ` : ''}${value}${unit ? ` ${unit}` : ''}`.trim();
    })
    .filter(Boolean)
    .join(' · ');
}
